import { API_BASE_URL } from '../config';

/**
 * WhatsApp Business API Service
 * 
 * This service handles communication with the WhatsApp Business API.
 * To use this service, you need to configure your WhatsApp Business API credentials
 * in the constructor below.
 */
class WhatsAppBusinessService {
  constructor() {
    this.apiUrl = API_BASE_URL;
    this.businessPhoneNumber = 'whatsapp:+1556470633'; // Your WhatsApp Business phone number
    this.businessAccountId = '708533315177953'; // Your WhatsApp Business Account ID
    this.phoneNumberId = '569837482890577'; // Your WhatsApp Phone Number ID
    this.accessToken = 'EAAMI6WsSjeUBO6iJ1HftZCWiypqCcNEIMvqiexg0fz5XlHetar0i7IkWI4uLLAFBz83QcjEUrf4ks9o2MWt7StoqgZBKNvnicBnZCEapMshZAsGmQIGZAD0stdlWcfr7WQZBYBSjcz7Mz7AMerXmUMPo9JKahFbX8MS80zZClFDavLcMNrCXmRDuXDpVZCj8a8cT1wZDZD'; // Your WhatsApp Business API access token
  }

  async checkServerHealth() {
    try {
      const response = await fetch(`${this.apiUrl}/api/health`);
      if (!response.ok) {
        throw new Error('Health check failed');
      }
      const data = await response.json();
      return data.status === 'healthy';
    } catch (error) {
      console.error('Server health check failed:', error);
      return false;
    }
  }

  async sendWhatsAppMessage(to, body, useTemplate = false) {
    try {
      // Check if credentials are set
      if (!this.accessToken || !this.phoneNumberId) {
        console.warn('WhatsApp Business API credentials not configured');
        
        // Fall back to the backend server if credentials aren't set
        const isHealthy = await this.checkServerHealth();
        if (!isHealthy) {
          return {
            success: false,
            error: 'Backend server is not available and WhatsApp credentials are not configured.'
          };
        }
        
        // Use the backend server
        const response = await fetch(`${this.apiUrl}/api/send`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 
            to: to.startsWith('whatsapp:') ? to : `whatsapp:${to}`, 
            body, 
            useTemplate 
          })
        });
        
        const data = await response.json();
        return this._handleResponse(response, data);
      }
      
      // Use WhatsApp Business API directly if credentials are set
      const formattedTo = to.startsWith('whatsapp:') ? to.replace('whatsapp:', '') : to;
      
      // Determine if we're using a template or sending a regular message
      let requestBody;
      
      if (useTemplate) {
        // For template messages, we expect specific parameters
        // If this is called from sendTemplateMessage, the body will be formatted as "patientName|practiceName"
        // Otherwise, we'll use default values
        let patientName = 'Patient';
        let practiceName = 'FollowApp Care';
        
        // Check if body contains template parameters
        if (body.includes('|')) {
          const parts = body.split('|');
          if (parts.length >= 2) {
            patientName = parts[0].trim();
            practiceName = parts[1].trim();
          }
        } else {
          // If no template parameters provided but template is requested,
          // use the message body as the patient name
          patientName = body.trim() || 'Patient';
        }
        
        // Using the fac_message template
        requestBody = {
          messaging_product: 'whatsapp',
          recipient_type: 'individual',
          to: formattedTo,
          type: 'template',
          template: {
            name: 'fac_message', // Template name as provided
            language: {
              code: 'en'
            },
            components: [
              {
                type: 'body',
                parameters: [
                  {
                    type: 'text',
                    text: patientName
                  },
                  {
                    type: 'text',
                    text: practiceName
                  }
                ]
              }
              // The buttons component is defined in the template and doesn't need parameters
            ]
          }
        };
      } else {
        // Regular text message
        requestBody = {
          messaging_product: 'whatsapp',
          recipient_type: 'individual',
          to: formattedTo,
          type: 'text',
          text: {
            body: body
          }
        };
      }
      
      // Make the API call to WhatsApp Business API
      const response = await fetch(`https://graph.facebook.com/v17.0/${this.phoneNumberId}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      return this._handleResponse(response, data);
    } catch (error) {
      console.error('Error sending WhatsApp message:', error);
      return {
        success: false,
        error: error.message || 'Network error occurred',
        details: 'Please check your connection and try again.'
      };
    }
  }

  async deleteConversation(phoneNumber) {
    try {
      const response = await fetch(`${this.apiUrl}/api/conversations/${phoneNumber}`, {
        method: 'DELETE'
      });

      const data = await response.json();
      return {
        success: data.status === 'success',
        error: data.status === 'error' ? data.message : null
      };
    } catch (error) {
      console.error('Error deleting conversation:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Method to handle incoming webhook from WhatsApp Business API
  handleIncomingMessage(body) {
    // WhatsApp Business API webhook format is different from Twilio
    const {
      from, // Phone number that sent the message
      to,   // Your WhatsApp Business phone number
      text, // Message content
      id,   // Message ID
      type, // Message type (text, image, etc.)
      timestamp
    } = body;

    return {
      from,
      to,
      body: text && text.body ? text.body : '', // WhatsApp Business API nests text content in a body property
      messageSid: id,
      numMedia: type !== 'text' ? 1 : 0,
      mediaUrl: type !== 'text' ? (body.image?.link || body.document?.link || null) : null,
      timestamp
    };
  }

  /**
   * Helper method to handle API responses
   * @param {Response} response - The fetch response object
   * @param {Object} data - The parsed JSON response data
   * @returns {Object} Standardized response object
   */
  _handleResponse(response, data) {
    if (!response.ok) {
      return {
        success: false,
        error: data.message || (data.error && data.error.message) || 'Failed to send message',
        details: data.error,
        debug: data.debug
      };
    }

    return {
      success: true,
      data: data.data || data,
      debug: data.debug
    };
  }
  
  /**
   * Helper method to create a template message with patient name and practice name
   * @param {string} phoneNumber - The recipient's phone number
   * @param {string} patientName - The patient's name
   * @param {string} practiceName - The practice name
   * @returns {Promise<Object>} - Response from the WhatsApp API
   */
  async sendTemplateMessage(phoneNumber, patientName, practiceName) {
    // Format the message as patientName|practiceName
    const messageBody = `${patientName}|${practiceName}`;
    return this.sendWhatsAppMessage(phoneNumber, messageBody, true);
  }
}

/**
 * WhatsApp Business API Configuration Guide
 * 
 * To use the WhatsApp Business API, you need to configure the following:
 * 
 * 1. In the constructor:
 *    - businessPhoneNumber: Your WhatsApp Business phone number with the 'whatsapp:' prefix
 *    - businessAccountId: Your WhatsApp Business Account ID
 *    - phoneNumberId: Your WhatsApp Phone Number ID from Meta
 *    - accessToken: Your WhatsApp Business API access token
 * 
 * 2. In config.js:
 *    - Update API_BASE_URL to point to your backend server
 * 
 * 3. Template Information:
 *    - The application uses the "fac_message" template
 *    - This template includes a button that links to "https://fol.ac/PvzWe"
 *    - To send a template message, use the sendTemplateMessage method with patient name and practice name
 *    - Example: whatsappBusinessService.sendTemplateMessage('+1234567890', 'John Doe', 'Dental Practice')
 */

// Create a singleton instance
const whatsappBusinessService = new WhatsAppBusinessService();
export default whatsappBusinessService;
